import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { schemas } from '@ciam/form-kit';
import { GigyaAPI } from '@ciam/gigya-api';
import { useTranslation } from '@ciam/i18n';
import { useCaptchaStore, useMfaStore, useTenureExpiredCheck } from '@features/login';
import { useLoginInfoStore } from '@features/shared';

const ERROR_CODES = {
  SUCCESS: 0,
  PENDING_REGISTRATION: 206001,
  CHANGE_PASSWORD: 403100,
  REQUIRED_CAPTCHA: 401020,
  WRONG_CAPTCHA: 401021,
  INVALID_PARAMETER_VALUE: 400006,
  ACCOUNT_LOCKED: 403120,
  EMAIL_OR_PASSWORD_INCORRECT: 403042,
} as const;

const LOGIN_SCHEMA = (isShowCaptcha: boolean) =>
  z.object({
    loginId: schemas.email,
    password: schemas.password,
    ...(isShowCaptcha ? { captchaToken: schemas.captchaToken } : {}),
  });

type useLoginFormType = z.infer<ReturnType<typeof LOGIN_SCHEMA>>;

const DEFAULT_VALUES = {
  loginId: '',
  password: '',
  captchaToken: '',
};

export const useLoginForm = () => {
  const { isShowCaptcha, showCaptcha, resetCaptcha } = useCaptchaStore();
  const { hasMfaSession, executeMfaLogin } = useMfaStore();
  const { setUserInfo } = useLoginInfoStore();
  const { t } = useTranslation(['login']);
  const { checkTenureExpired } = useTenureExpiredCheck();

  const methods = useForm<useLoginFormType>({
    mode: 'onChange',
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(LOGIN_SCHEMA(isShowCaptcha)),
  });
  const { handleSubmit, setError } = methods;

  const handleSetRootError = (message: string) => {
    return setError('root', {
      type: 'manual',
      message: message,
    });
  };

  const onSubmit = handleSubmit(async (formData) => {
    try {
      const { loginId, password, captchaToken } = formData;

      // 1. 로그인
      const { data: loginData } = await GigyaAPI.accounts.login({
        loginID: loginId,
        password,
        include: 'data',
        ...(captchaToken ? { captchaToken, captchaType: 'recaptcha_v2' } : {}),
      });

      switch (loginData.errorCode) {
        case ERROR_CODES.SUCCESS: {
          const loginInfo = {
            email: loginId,
            uid: loginData.UID,
            uidSignature: loginData.UIDSignature,
            signatureTimestamp: loginData.signatureTimestamp,
            isAdLogin: false,
          };

          await setUserInfo(loginInfo);

          const { email, uid, uidSignature, signatureTimestamp } = loginInfo;

          if (!hasMfaSession({ email })) {
            const jwtResponse = await executeMfaLogin({
              email,
              uid,
              uidSignature,
              signatureTimestamp,
            });

            return window.location.assign(jwtResponse);
          }

          // 1. 재직 승인 여부 체크
          await checkTenureExpired({ uid: loginData.UID });
          // 2. 로그인
          return await GigyaAPI.socialize.notifyLogin({
            dontHandleScreenSet: true,
            siteUID: uid,
            UIDSig: uidSignature,
            UIDTimestamp: signatureTimestamp,
          });
        }
        case ERROR_CODES.PENDING_REGISTRATION:
          return handleSetRootError(t('errors.pending-registration'));
        case ERROR_CODES.WRONG_CAPTCHA:
          resetCaptcha();
          return handleSetRootError(t('errors.wrong-captcha'));
        case ERROR_CODES.REQUIRED_CAPTCHA:
          showCaptcha();
          return handleSetRootError(t('errors.required-captcha'));
        case ERROR_CODES.CHANGE_PASSWORD:
          return handleSetRootError(t('errors.change-password'));
        case ERROR_CODES.INVALID_PARAMETER_VALUE:
          return handleSetRootError(t('errors.invalid-parameter-value'));
        case ERROR_CODES.ACCOUNT_LOCKED:
          return handleSetRootError(t('errors.account-locked'));
        case ERROR_CODES.EMAIL_OR_PASSWORD_INCORRECT:
          return handleSetRootError(t('errors.email-or-password-incorrect'));
        default:
          return handleSetRootError(t('errors.default'));
      }
    } catch (error) {
      return handleSetRootError(`${t('errors.default')} ${error}`);
    }
  });

  return {
    ...methods,
    onSubmit,
  };
};
